import axios, { AxiosInstance } from 'axios';
import { SignupModel, SignupModelHelper } from "../data/SignupModel";
import { DeficiencyDto } from '../data/dto/deficiency.dto';
import { LegacyCookies } from '../data/dto/legacy-cookies.dto';
import { NormalizedRoleDto, RoleDto } from '../data/dto/role.dto';
import { SalaryExpectationDto } from '../data/dto/salary-expectation.dto';
import { ServiceResponse } from "./ServiceModels";
import { UserService } from './UserService';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL
});

export default class AmarelinhoService {
  private static _cvApi: AxiosInstance;
  private static _authRequest: AxiosInstance;

  private static get cookies(): LegacyCookies {
    return UserService.getLoggedUser().cookie;
  }

  private static get authRequest() {
    if (!this._authRequest) {
      const user = UserService.getLoggedUser();
      this._authRequest = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: { Authorization: "Bearer " + user.token }
      });
    }
    return this._authRequest;
  }

  private static get cvApi() {
    if (!this._cvApi) {
      const user = UserService.getLoggedUser();
      this._cvApi = axios.create({
        baseURL: `${process.env.REACT_APP_API_URL}/user/${user.id}/curriculum`,
        headers: { Authorization: "Bearer " + user.token }
      });
    }
    return this._cvApi;
  }

  static async validateEmail(email: string): Promise<ServiceResponse> {
    let data: ServiceResponse = {
      success: true
    };

    try {
      const validation = await api.post('/user/validate-email', {
        email
      });

      if (!validation.data.success) {
        data = {
          message: validation.data.message,
          success: false
        };
      }
    } catch (ex) {
      console.log({ location: 'ValidationService::validateEmail', ex });
      data = {
        message: 'Estou com problemas para validar seu e-mail.  Favor tente mais tarde.',
        success: false
      };
    }

    return data;
  }

  static async validateCPF(cpf: string, nome: string): Promise<ServiceResponse> {
    let data: ServiceResponse = {
      success: true
    };

    try {
      const validation = await api.post('/user/validate-cpf', {
        cpf
      });

      if (!validation.data.success) {
        data = {
          message: validation.data.message,
          success: false
        };
      }
    } catch (ex) {
      console.log({ location: 'ValidationService::validateCPF', ex });
      data = {
        message: 'Estou com problemas para validar seu CPF.  Favor tente mais tarde.',
        success: false
      };
    }

    return data;
  }

  static async getAddressByZipCode(zip: string): Promise<any> {
    let data: ServiceResponse = {
      success: true
    };

    try {
      const response = await axios.get(`https://viacep.com.br/ws/${zip}/json/`);

      if (response.data.erro) {
        data = {
          success: false,
          message: 'Estou com problemas para validar seu CEP.  Favor tente mais tarde.'
        };
      } else {
        data = {
          ...response.data,
          success: true
        }
      }
    } catch (ex) {
      console.log({ location: 'ValidationService::getAddressByZipCode', ex });
      data = {
        message: 'Estou com problemas para validar seu CEP.  Favor tente mais tarde.',
        success: false
      };
    }

    return data;
  }

  static async listDeficiencies(): Promise<DeficiencyDto[] | null> {
    try {
      const response = await this.authRequest.get(`/deficiencies`);
      return response.data;
    } catch (ex) {
      console.log({ location: 'AmarelinhoService::listDeficiencies', ex });
    }

    return null;
  }
  static async listNationalities(): Promise<string[] | null> {

    try {
      const response = await this.authRequest.get(`/nationalities`);
      return response.data;
    } catch (ex) {
      console.log({ location: 'AmarelinhoService::listNationalities', ex });
    }

    return null;
  }

  static async saveExtraPersonalData(signup: SignupModel): Promise<ServiceResponse> {
    let data: ServiceResponse = {
      success: true
    };

    try {
      await this.cvApi.post(`/personal-data`, {
        cookies: this.cookies,
        personalData: {
          nationality: signup.nationality,
          maritalStatus: signup.maritalStatus.value,
          deficiencyId: signup.deficiency?.id
        }
      });
    } catch (ex) {
      console.log({ location: 'ValidationService::saveExtraPersonalData', ex });
      data = {
        message: 'Estou com problemas para salvar seu cadastro. Favor tente mais tarde.',
        success: false
      };
    }

    return data;
  }

  static async saveAccount(signup: SignupModel): Promise<any> {
    let data: ServiceResponse = {
      success: true
    };

    try {
      const response = await api.post('/user', SignupModelHelper.toSignUpDto(signup));

      if (!response.data.success) {
        data = {
          message: response.data.message ?? 'Estou com problemas para salvar seu cadastro.  Favor tente mais tarde.',
          success: false
        };
      } else {
        data = {
          ...response.data,
          success: true
        }
        UserService.setLoggedUser(response.data);
      }
    } catch (ex) {
      console.log({ location: 'ValidationService::saveAccount', ex });
      data = {
        message: 'Estou com problemas para salvar seu cadastro.  Favor tente mais tarde.',
        success: false
      };
    }

    return data;
  }

  static async listSalaryExpectations(): Promise<SalaryExpectationDto[] | null> {
    try {
      const response = await this.authRequest.get(`/curriculum/salary-expectations`);
      if (response.data.errorMessage) {
        throw response.data;
      }
      return response.data;
    } catch (ex) {
      console.log({ location: 'AmarelinhoService::listSalaryExpectations', ex });
    }

    return null;
  }

  static async saveSalaryExpectation(signup: SignupModel): Promise<ServiceResponse> {
    let data: ServiceResponse = {
      success: true
    };

    try {
      await this.cvApi.post(`/salary-expectation`, {
        salaryExpectationId: signup.salaryExpectation.id
      });
    } catch (ex) {
      console.log({ location: 'ValidationService::saveSalaryExpectation', ex });
      data = {
        message: 'Estou com problemas para salvar seu cadastro.  Favor tente mais tarde.',
        success: false
      };
    }

    return data;
  }

  static async saveExperiences(signup: SignupModel): Promise<any> {
    let data: ServiceResponse = {
      success: true
    };

    try {
      const experiences = SignupModelHelper.toAmarelinhoExperiencesDto(signup);
      await this.cvApi.post(`/experiences`, { experiences, cookies: this.cookies });
    } catch (ex) {
      console.log({ location: 'ValidationService::saveExperiences', ex });
      data = {
        message: 'Estou com problemas para salvar seu cadastro.  Favor tente mais tarde.',
        success: false
      };
    }

    return data;
  }

  static async saveCvExtraData(signup: SignupModel): Promise<ServiceResponse> {
    let data: ServiceResponse = {
      success: true
    };

    try {
      const experiences = SignupModelHelper.toAmarelinhoExperiencesDto(signup);
      await this.cvApi.post(`/experiences`, { experiences, cookies: this.cookies });
    } catch (ex) {
      console.log({ location: 'ValidationService::saveCvExtraData', ex });
      data = {
        message: 'Estou com problemas para salvar seu cadastro.  Favor tente mais tarde.',
        success: false
      };
    }

    return data;
  }


  static async saveObjectives(signup: SignupModel): Promise<ServiceResponse> {
    let data: ServiceResponse = {
      success: true
    };

    try {
      const desiredPositions = SignupModelHelper.toAmarelinhoDesiredPositionsDto(signup);
      await this.cvApi.post(`/desired-positions`, {
        desiredPositions,
        cookies: this.cookies
      });
    } catch (ex) {
      console.log({ location: 'ValidationService::saveObjectives', ex });
      data = {
        message: 'Estou com problemas para salvar seu cadastro. Favor tente mais tarde.',
        success: false
      };
    }

    return data;
  }

  static async saveLanguages(signup: SignupModel): Promise<any> {
    let data: ServiceResponse = {
      success: true
    };

    try {
      const languages = SignupModelHelper.toAmarelinhoLanguageDto(signup);
      await this.cvApi.post(`/languages`, {
        languages,
        cookies: this.cookies
      });
    } catch (ex) {
      console.log({ location: 'ValidationService::saveLanguages', ex });
      data = {
        message: 'Estou com problemas para salvar seu cadastro.  Favor tente mais tarde.',
        success: false
      };
    }

    return data;
  }

  static async savePresentationLetter(signup: SignupModel): Promise<any> {
    let data: ServiceResponse = {
      success: true
    };

    try {
      const presentationLetter = SignupModelHelper.toAmarelinhoPresentationLetterDto(signup);
      await this.cvApi.post('/presentation-letter', { presentationLetter, cookies: this.cookies });
    } catch (ex) {
      console.log({ location: 'ValidationService::savePresentationLetter', ex });
      data = {
        message: 'Estou com problemas para salvar seu cadastro.  Favor tente mais tarde.',
        success: false
      };
    }

    return data;
  }

  static async saveGraduation(signup: SignupModel): Promise<ServiceResponse> {
    let data: ServiceResponse = {
      success: true
    };

    try {
      const schoolingList = SignupModelHelper.toAmarelinhoSchoolingListDto(signup);
      await this.cvApi.post('/schooling', { schoolingList, cookies: this.cookies });
    } catch (ex) {
      console.log({ location: 'ValidationService::saveGraduation', ex });
      data = {
        message: 'Estou com problemas para salvar seu cadastro.  Favor tente mais tarde.',
        success: false
      };
    }

    return data;
  }

  static async saveCourses(signup: SignupModel): Promise<ServiceResponse> {
    let data: ServiceResponse = {
      success: true
    };

    try {
      const courses = SignupModelHelper.toAmarelinhoCoursesDto(signup);
      await this.cvApi.post('/courses', { courses, cookies: this.cookies });
    } catch (ex) {
      console.log({ location: 'ValidationService::saveCourses', ex });
      data = {
        message: 'Estou com problemas para salvar seu cadastro. Favor tente mais tarde.',
        success: false
      };
    }

    return data;
  }


  static async isLogado(): Promise<any> {
    let data: any = {
      success: true
    };

    try {
      const loggedUser = UserService.getLoggedUser();
      if (!loggedUser) {
        data = {
          message: 'Usuário não está logado.',
          success: false
        };
      } else {
        const { cookie: { authCookie, sessionCookie }, token } = loggedUser;

        const logado = await api.post('/auth/session/validate', {
          authCookie, sessionCookie, token
        });
        if (logado.data.loggedIn === true) {
          data = {
            userId: loggedUser.id,
            success: true
          }
        } else {
          data = {
            message: 'Usuário não está logado.',
            success: false
          };
        }
      }
    } catch (ex) {
      console.log({ location: 'ValidationService::isLogado', ex });
      data = {
        message: 'Usuário não está logado.',
        success: false
      };
    }

    return data;
  }

  static async getPositions(query: string): Promise<RoleDto[] | null> {
    try {

      const response = await api.get(`/role-tips?search=${query}&limit=20`);
      return response.data;
    } catch (ex) {
      console.log({ location: 'AmarelinhoService::getPositions', ex });
    }

    return null;
  }

  static async getNormalizedRoles(): Promise<NormalizedRoleDto[] | null> {
    try {

      const response = await api.get(`/role/sge`);
      return response.data;
    } catch (ex) {
      console.log({ location: 'AmarelinhoService::getNormalizedRoles', ex });
    }

    return null;
  }

}
